import React, { useMemo } from 'react';
import { StyleSheet, Pressable, Platform } from 'react-native';
import { YStack } from '@tamagui/stacks';
import { useThemeName } from '@tamagui/core';
import { useNavigation } from '@react-navigation/native';
import { ChevronLeft } from 'lucide-react-native';
import { COLORS } from '@/constants/colors';
import { SCREEN_WIDTH, HERO_HEIGHT } from '../constants';
import { useIsMobileLayout } from '@/hooks/useWindowWidth';
import AuthenticatedImage from '@/components/AuthenticatedImage';

type HeroImageProps = {
  imageUri: string;
};

const HeroImage = React.memo<HeroImageProps>(({ imageUri }) => {
  const navigation = useNavigation();
  const themeName = useThemeName();
  const isDark = themeName === 'dark';
  const isMobileLayout = useIsMobileLayout();
  
  const styles = useMemo(() => {
    const useMobileLayout = Platform.OS !== 'web' || isMobileLayout;
    
    return StyleSheet.create({
      heroImage: {
        width: useMobileLayout ? SCREEN_WIDTH : '100%',
        height: useMobileLayout ? HERO_HEIGHT : '100%',
        backgroundColor: isDark ? COLORS.ZINC_900 : COLORS.ZINC_200,
        position: 'relative',
      },
      heroImageFull: {
        width: '100%',
        height: '100%',
      },
      backButton: {
        position: 'absolute',
        top: 45,
        left: 45,
        zIndex: 10,
      },
      backButtonInner: {
        width: 44,
        height: 44,
        borderRadius: 8,
        backgroundColor: 'rgba(0, 0, 0, 0.05)',
        borderWidth: 1,
        borderColor: `rgba(255, 255, 255, 0.5)`,
        justifyContent: 'center',
        alignItems: 'center',
      },
    });
  }, [isDark, isMobileLayout]);
  
  return (
    <YStack style={styles.heroImage}>
      <AuthenticatedImage 
        uri={imageUri}
        style={styles.heroImageFull} 
        resizeMode="cover" 
      />
      
      {/* Back Button - hidden on web desktop (shown on mobile and web mobile layout) */}
      {(Platform.OS !== 'web' || isMobileLayout) && (
        <Pressable 
          onPress={() => navigation.goBack()}
          style={styles.backButton}
        >
          <YStack style={styles.backButtonInner}>
            <ChevronLeft size={28} color={COLORS.WHITE} strokeWidth={1} />
          </YStack>
        </Pressable>
      )}
    </YStack>
  );
});

HeroImage.displayName = 'HeroImage';

export default HeroImage;

